import { dbRoomRef, type BuiltForm } from "@sts/schemas";
import type { AdjacencyConfirmation, SurveyRoom, SurveySurface } from "../api/types.js";

/**
 * The Survey page's words for "what is on the other side of this wall?".
 *
 * The scan conversion calls a wall external when nothing was scanned behind
 * it. On a terrace or a semi that includes the party walls, which lose far
 * less heat, so the heat loss is too high until somebody says otherwise. The
 * API marks those walls (`adjacencyAssumed`) and takes the answer at
 * `POST /api/surfaces/:id/adjacency`; this module is the text around them.
 */

/**
 * Where the temperature comes from, said once and the same way everywhere.
 *
 * MIS 3005-D:2025 sets no figure for either space — §3.4.1(a) defers to
 * BS EN 12831-1:2017 — and MCS's own Heat Load Calculator takes both from the
 * CIBSE Domestic Heating Design Guide (2026). The page therefore asks, and
 * never fills one in. docs/research/party-walls-2026-09-11.md.
 */
export const ADJACENCY_TEMPERATURE_SOURCE =
  "MCS doesn't set this figure: MIS 3005-D §3.4.1(a) defers to BS EN 12831-1, and MCS's " +
  "heat load calculator takes it from the CIBSE Domestic Heating Design Guide (2026). " +
  "Enter the temperature your design guide gives for the space on the other side.";

export type WallChoice = "party" | "unheated-space";

export const WALL_CHOICE_LABEL: Readonly<Record<WallChoice, string>> = {
  party: "Party wall (adjoining home)",
  "unheated-space": "Unheated space (garage, store…)",
};

/** How many party walls a built form usually has, and how to say it. */
const PARTY_WALLS_USUALLY: Readonly<Record<BuiltForm, string>> = {
  detached:
    "A detached house has no party walls. A wall to an attached garage or store is an unheated space.",
  "semi-detached": "A semi-detached house usually has one party wall, shared with the other half.",
  "end-terrace": "An end-terrace usually has one party wall, shared with the house next door.",
  "mid-terrace": "A mid-terrace usually has two party walls, one to each neighbour.",
  "enclosed-end-terrace": "An enclosed end-terrace usually has two party walls.",
  "enclosed-mid-terrace":
    "An enclosed (back-to-back) mid-terrace usually has three party walls; only the front is outside.",
};

/**
 * What the built form suggests — a suggestion, never a change. A party wall
 * usually runs through more than one room, so the count is of the house's
 * walls, not of the rows below; the second sentence says so where it matters.
 */
export function builtFormSuggestion(builtForm: BuiltForm | null | undefined): string {
  if (builtForm === null || builtForm === undefined) {
    return "Record the built form under Property to see how many party walls to expect.";
  }
  const usually = PARTY_WALLS_USUALLY[builtForm];
  return builtForm === "detached"
    ? usually
    : `${usually} Each one may run through several rooms.`;
}

/**
 * Walls still converted as outside only because nothing was scanned behind
 * them.
 *
 * WALLS ONLY. The API marks floors the conversion assumed were solid and on
 * the ground with the same flag, which is a real question and a different one
 * — it is asked in the floor's own panel, and counting a floor here would make
 * the heat-loss notice say "counts N walls as outside that may be party walls"
 * about a floor.
 */
export function assumedOutsideWalls(
  rooms: readonly SurveyRoom[],
): { readonly room: SurveyRoom; readonly surface: SurveySurface }[] {
  return rooms.flatMap((room) =>
    room.surfaces
      .filter((s) => s.kind === "wall" && s.adjacencyAssumed === true)
      .map((surface) => ({ room, surface })),
  );
}

/** A heat-loss wall and its room, as the scan's Walls table finds it. */
export interface HeatLossWall {
  readonly room: SurveyRoom;
  readonly surface: SurveySurface;
}

/**
 * Finds the heat-loss wall behind a wall on the plan or in the 3D view.
 *
 * The scan conversion writes each room's `externalRef` as the scanned room's
 * (`ScanRoomGroup.externalRef`) and each wall's as the RoomPlan element id
 * the plan shows, so the pair names the same wall on both sides. Matched on
 * BOTH, as the API matches its review list, so a wall id repeated across
 * rooms cannot pick up another room's answer. A wall with no heat-loss twin —
 * added on the plan after the rooms were converted, a room typed in, or a
 * newer scan the survey has not taken — finds nothing, and is asked nothing.
 */
export function heatLossWallFinder(
  rooms: readonly SurveyRoom[],
  /**
   * The storey being drawn, where the survey has more than one.
   *
   * A MERGED FLOOR'S ROOMS ARE NOT CALLED WHAT THE CAPTURE CALLS THEM. Merging
   * two walks (0069) namespaces the moved rooms by the floor they became —
   * `L1:<ref>` — while the capture the plan is drawn from still says the bare
   * ref, because it knows nothing of the merge. Matching one against the other
   * found nothing for an ENTIRE floor, so on a merged property the whole
   * upstairs could not be answered for party walls at all: every wall up there
   * was silently treated as having no heat-loss twin, which is also what a
   * hand-drawn wall looks like, so nothing said anything was wrong.
   *
   * Null means the survey's own capture, which is the lowest floor and is
   * never namespaced — so level 0, and `dbRoomRef` falls back to the bare ref.
   */
  storey: number | null = null,
): (roomRef: string | undefined, wallId: string) => HeatLossWall | undefined {
  const known = new Set(
    rooms.flatMap((room) => (room.externalRef ? [room.externalRef] : [])),
  );
  const byRoom = new Map<string, Map<string, HeatLossWall>>();
  for (const room of rooms) {
    if (!room.externalRef) continue;
    const walls = byRoom.get(room.externalRef) ?? new Map<string, HeatLossWall>();
    for (const surface of room.surfaces) {
      if (surface.kind === "wall" && surface.externalRef) walls.set(surface.externalRef, { room, surface });
    }
    byRoom.set(room.externalRef, walls);
  }
  return (roomRef, wallId) =>
    roomRef === undefined
      ? undefined
      : byRoom.get(dbRoomRef(storey ?? 0, roomRef, known))?.get(wallId);
}

/**
 * The heat-loss room behind a room on the drawing, by the same rule.
 *
 * Exported so the two matches cannot drift: the panel finds the ROOM and the
 * check finds its WALL, and before this they resolved the identifier
 * differently — which is how one of them could work on a merged floor while
 * the other did not.
 */
export function heatLossRoomFor(
  rooms: readonly SurveyRoom[],
  roomRef: string | undefined,
  storey: number | null = null,
): SurveyRoom | undefined {
  if (roomRef === undefined) return undefined;
  const known = new Set(
    rooms.flatMap((room) => (room.externalRef ? [room.externalRef] : [])),
  );
  const wanted = dbRoomRef(storey ?? 0, roomRef, known);
  return rooms.find((room) => room.externalRef === wanted);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * "A. Owner, 11 Sep" — who confirmed and when, in UK time, in the same short
 * form the calibration marker uses. Either half is dropped when unknown.
 */
export function confirmationByline(confirmation: AdjacencyConfirmation): string {
  const words = (confirmation.byName ?? "").trim().split(/\s+/).filter((w) => w.length > 0);
  const who =
    words.length === 0
      ? null
      : words.length === 1
        ? words[0]!
        : `${words[0]!.charAt(0).toUpperCase()}. ${words[words.length - 1]}`;
  let when: string | null = null;
  const date = new Date(confirmation.at);
  if (!Number.isNaN(date.getTime())) {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      day: "numeric",
      month: "numeric",
    }).formatToParts(date);
    const day = parts.find((p) => p.type === "day")?.value;
    const month = Number(parts.find((p) => p.type === "month")?.value);
    if (day && month >= 1 && month <= 12) when = `${day} ${MONTHS[month - 1]}`;
  }
  return [who, when].filter((p): p is string => p !== null).join(", ");
}

/** A typed temperature: a finite number in the band the API accepts, or why not. */
export function parseAdjacencyTemperature(raw: string): { value: number } | { error: string } {
  const trimmed = raw.trim().replace(/\s*°?\s*C?$/i, "").replace("−", "-");
  if (trimmed === "") return { error: "Enter the temperature on the other side." };
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return { error: "The temperature has to be a number, in °C." };
  if (value < -50 || value > 50) return { error: "That is outside −50 to 50 °C — check the units." };
  return { value };
}
